import { WASTE_METRIC } from "../constants/wasteTaxonomy";

const norm = (v) => String(v || "").trim().toLowerCase();

export const isWasteMetric = (esgMetric) => norm(esgMetric) === norm(WASTE_METRIC);

const escapeHtml = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/**
 * Turns the API's waste preflight payload (WastePreflightResponse) into a short summary the
 * upload page shows before running. Pure; returns both plain lines and an HTML fragment.
 *
 *   { totalRows, resolvedCount, willFail:[{rowNumber, errors[]}], needsReview:[{rowNumber, wasteType, disposalMethodRaw, reason}], warnings:[...] }
 */
export function buildWastePreflightSummary(preflight, maxDetails = 6) {
  const p = preflight || {};
  const willFail = Array.isArray(p.willFail) ? p.willFail : [];
  const needsReview = Array.isArray(p.needsReview) ? p.needsReview : [];
  const warnings = Array.isArray(p.warnings) ? p.warnings : [];
  const total = Number(p.totalRows) || 0;
  const resolved = Number(p.resolvedCount) || 0;

  const lines = [];
  lines.push(`${resolved} of ${total} row(s) will calculate immediately.`);
  if (needsReview.length > 0) lines.push(`${needsReview.length} row(s) will be saved for review (no emission factor could be resolved).`);
  if (willFail.length > 0) lines.push(`${willFail.length} row(s) will be rejected.`);
  if (p.libraryEdition) lines.push(`Factor library: ${p.libraryEdition}.`);

  const details = [];
  willFail.slice(0, maxDetails).forEach((f) => details.push(`Row ${f.rowNumber}: ${(f.errors || []).join("; ")}`));
  needsReview.slice(0, Math.max(0, maxDetails - details.length)).forEach((r) =>
    details.push(`Row ${r.rowNumber}: ${r.wasteType || "?"} via "${r.disposalMethodRaw || "—"}" — ${r.reason || "needs review"}`)
  );
  const hidden = willFail.length + needsReview.length - details.length;
  if (hidden > 0) details.push(`…and ${hidden} more.`);

  const html =
    `<div style="text-align:left;font-size:14px">` +
    lines.map((l) => `<p style="margin:4px 0">${escapeHtml(l)}</p>`).join("") +
    (details.length ? `<ul style="margin:8px 0 0 18px;padding:0;font-size:13px;color:#555">${details.map((d) => `<li>${escapeHtml(d)}</li>`).join("")}</ul>` : "") +
    (warnings.length ? `<p style="margin:8px 0 0;font-size:12px;color:#888">${warnings.length} row(s) carry warnings; they still calculate.</p>` : "") +
    `</div>`;

  return {
    total,
    resolved,
    reviewCount: needsReview.length,
    failCount: willFail.length,
    needsAttention: needsReview.length + willFail.length > 0,
    lines,
    details,
    html,
  };
}
